import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { categorySchema } from '../menu/categorySchema';
import { menuItemSchema } from '../menu/menuItemSchema';
import { zPrice, zTextNullable } from '../primitives';
import { orderItemModifierSchema } from './orderItemModifierSchema';

export const orderItemSchema = z.object({
  categoryName: categorySchema.shape.name,
  categoryUuid: z.string(), // Note: Don't use `categorySchema.shape.uuid` as it uses default value which makes input type optional
  discountPercent: z.number().nullable().default(null),
  discountTotal: z.number().default(0),
  /** `lastPrintedAt` is used by POS only (this is why it is `nullable`) */
  lastPrintedAt: z.number().nullable().default(null),
  /**
   * `lastPrintedQty` is used by POS only (this is why it is `nullable`)
   * - Used to track the qty of items printed on kitchen dockets.
   * - When `lastPrintedQty > 0, this means the item has been printed at least once on a kitchen docket
   *   and the item cannot be directly removed (it must be voided instead - so the kitchen gets notified of this change).
   * - Used to calculating what needs to be printed on subsequent dockets.
   * - If an order is "closed", then this value is set/updated. This applies to re-prints of "closed" orders too.
   */
  lastPrintedQty: z.number().nullable().default(null),
  localCreatedAt: z.number().default(() => Date.now()),
  /**
   * `localUpdatedAt` is a timestamp representing when the POS last committed this orderItem to its local storage.
   *
   * Behavior:
   * - During cart modifications: Updated to track in-memory changes (serves as change detection)
   * - During order save: Updated to current time for items where `orderItem.localUpdatedAt` > `order.localUpdatedAt`
   * - After save completion: Represents when this item was last committed to storage (same semantic as `order.localUpdatedAt`)
   *
   * This field is updated when:
   * - Item is modified (qty, price, modifiers, notes, etc.) - tracks change
   * - Item is printed (lastPrintedAt, lastPrintedQty updated)
   * - Order containing this item is saved to storage - commits change
   *
   * Use cases:
   * - Identifying which items have uncommitted modifications
   * - Tracking when items were last persisted locally
   * - Automatic change detection during order save operations
   * - Sync conflict resolution for individual items
   */
  localUpdatedAt: z.number().default(() => Date.now()),
  menuItemComesWithModifiersUuids: menuItemSchema.shape.comesWithModifiersUuids,
  menuItemDescription: menuItemSchema.shape.description,
  menuItemIsGstExempt: menuItemSchema.shape.isGstExempt,
  menuItemModifierGroupUuids: menuItemSchema.shape.modifierGroupUuids,
  menuItemName: menuItemSchema.shape.name,
  menuItemPrice: menuItemSchema.shape.price,
  menuItemSizeModifierGroupUuid: menuItemSchema.shape.sizeModifierGroupUuid,
  menuItemUuid: z.string(), // Note: Don't use `menuItemSchema.shape.uuid` as it uses default value which makes input type optional
  modifiers: z.array(orderItemModifierSchema).default([]),
  notes: z.string().max(60).nullable().default(null),
  qty: z.number().default(1),
  subtotal: z.number().default(0),
  surchargePercent: z.number().nullable().default(null),
  surchargeTotal: z.number().default(0),
  /** Don't set `total` default to make sure we explicitly calculate. */
  total: z.number(),
  uuid: z.string().default(() => uuidv4()),
  /**
   * `voidedAt` is used by POS only.
   * After an order item has been printed on a kitchen docket, it cannot be removed (it must be voided instead).
   **/
  voidedAt: z.number().nullable().default(null),
});
export type OrderItem = z.output<typeof orderItemSchema>;

/** Strict schema */
export const orderItemStrictSchema = orderItemSchema
  .safeExtend({
    categoryUuid: z.guid(),
    discountTotal: zPrice,
    menuItemUuid: z.guid(),
    notes: zTextNullable.pipe(z.string().max(60).nullable()),
    subtotal: zPrice,
    surchargeTotal: zPrice,
    total: zPrice,
    uuid: z.guid(),
  })
  /** `price` OR `sizeModifierGroupId` must be set. */
  .superRefine((orderItem, ctx) => {
    if (orderItem.menuItemSizeModifierGroupUuid && orderItem.modifiers.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: `'${orderItem.menuItemName}' is missing a size modifier'`,
        path: ['modifiers'],
      });
    }
  });
